/**
 * Per-session "the doctor pressed Complete" flag, scoped to this browser.
 *
 * Why it exists: when Complete fires while the backend / socket is down,
 * the FE can't reach the gateway to run synth, and the recording_sessions
 * row stays in `paused` (or `stopped`) without a medicalRecordId. On the
 * next dock mount we need to know whether to (a) leave the session alone
 * because the doctor is still recording and just paused, or (b) auto-fire
 * recovery synth because they tried to finish but couldn't.
 *
 * The signal lives in localStorage so it survives refresh and tab close
 * but not browser-data clearing — same trade-off as the IDB chunk queue.
 * The `set` call MUST happen before the async endRecording awaits begin
 * so a navigation away or hard reload after that point still leaves a
 * recoverable trace.
 */

const KEY_PREFIX = 'maie.completionIntent.';

const key = (sessionId: string) => `${KEY_PREFIX}${sessionId}`;

export const setCompletionIntent = (sessionId: string): void => {
  try {
    localStorage.setItem(key(sessionId), String(Date.now()));
  } catch {
    /* localStorage disabled — fall through; the user just won't get
       auto-recovery on the next mount, but everything else still works. */
  }
};

export const hasCompletionIntent = (sessionId: string): boolean => {
  try {
    return localStorage.getItem(key(sessionId)) !== null;
  } catch {
    return false;
  }
};

export const clearCompletionIntent = (sessionId: string): void => {
  try {
    localStorage.removeItem(key(sessionId));
  } catch {
    /* see setCompletionIntent — non-fatal */
  }
};
